import * as y from 'yup';
import { BorrowerOrCoBorrowerType } from 'src/types';
import { EmploymentContract } from 'src/components/Loan/Application/Sections/EmploymentInfo/Inputs/Employments/store/types';
import { URLAAlternateNameContract } from 'src/components/Loan/Application/Sections/BorrowerInfo/Inputs/AlternateNames/store/types';
import { ResidenceContract } from 'src/components/Loan/Application/Sections/BorrowerInfo/Inputs/Residence/store/types';
import { fieldIds } from 'src/util';
import { ValidationLoan } from 'src/types/loan';

export enum PriorPropertyUsageType {
  PrimaryResidence = 'PrimaryResidence',
  FHASecondaryResidence = 'FHASecondaryResidence',
  SecondaryResidence = 'SecondaryResidence',
  Investment = 'Investment',
}

export enum PriorPropertyTitleType {
  Sole = 'Sole',
  JointWithSpouse = 'JointWithSpouse',
  JointWithOtherThanSpouse = 'JointWithOtherThanSpouse',
}

export enum MaritalStatusType {
  Married = 'Married',
  Unmarried = 'Unmarried',
  Separated = 'Separated',
}

export enum ResidencyStatusType {
  USCitizen = 'USCitizen',
  PermanentResidentAlien = 'PermanentResidentAlien',
  NonPermanentResidentAlien = 'NonPermanentResidentAlien',
}

export const BorrowerContract = y.object({
  id: y.string().uuid(),
  applicantType: y
    .mixed<BorrowerOrCoBorrowerType>()
    .oneOf(Object.values(BorrowerOrCoBorrowerType)),
  firstName: y.string().required(),
  middleName: y.string().optional(),
  lastName: y.string().required(),
  suffix: y.string().optional(),
  emailAddressText: y.string().required(),
  homePhoneNumber: y.string().required(),
  homeownerPastThreeYearsIndicator: y.boolean(),
  priorPropertyUsageType: y
    .mixed<PriorPropertyUsageType>()
    .oneOf(Object.values(PriorPropertyUsageType))
    .optional()
    .when('homeownerPastThreeYearsIndicator', (val) => {
      if (val)
        return y
          .mixed<PriorPropertyUsageType>()
          .oneOf(Object.values(PriorPropertyUsageType))
          .required();
      return y
        .mixed<PriorPropertyUsageType>()
        .oneOf(Object.values(PriorPropertyUsageType))
        .optional();
    }),
  priorPropertyTitleType: y
    .mixed<PriorPropertyTitleType>()
    .oneOf(Object.values(PriorPropertyTitleType))
    .optional()
    .when('homeownerPastThreeYearsIndicator', (val) => {
      if (val)
        return y
          .mixed<PriorPropertyTitleType>()
          .oneOf(Object.values(PriorPropertyTitleType))
          .required();
      return y
        .mixed<PriorPropertyTitleType>()
        .oneOf(Object.values(PriorPropertyTitleType))
        .optional();
    }),
  birthDate: y.date().required().max(new Date()),
  maritalStatusType: y
    .mixed<MaritalStatusType>()
    .oneOf(Object.values(MaritalStatusType))
    .required(),
  schoolingTermYears: y.number().positive().required(),
  dependentsAgesDescription: y.string().optional(),
  urlaAlternateNames: y.array(URLAAlternateNameContract),
  residences: y.array(ResidenceContract),
  employment: y.array(EmploymentContract).min(1).required(),
  intentToOccupyIndicator: y.boolean().optional(),
  mailingAddressSameAsPresentIndicator: y.boolean().optional(),
  mailingAddress: y
    .object({
      addressCity: y.string().optional(),
      addressPostalCode: y.string().optional(),
      addressState: y.string().optional(),
      addressStreetLine1: y.string().optional(),
      urla2020StreetAddress: y.string().optional(),
      country: y.string().optional(),
    })
    .optional()
    .when(
      ['applicantType', '$loan'],
      // @ts-ignore
      (
        [applicantType, loan]: [BorrowerOrCoBorrowerType, ValidationLoan],
        schema,
      ) => {
        if (
          (applicantType === BorrowerOrCoBorrowerType.Borrower &&
            !loan?.currentApplication?.borrower
              .mailingAddressSameAsPresentIndicator) ||
          (applicantType === BorrowerOrCoBorrowerType.CoBorrower &&
            loan?.customFields?.[fieldIds.hasCoborrower] === 'true' &&
            loan?.customFields?.[fieldIds.sameMailingAddressAsBorrower] !== 'Y')
        ) {
          return y.object({
            addressCity: y.string().required(),
            addressPostalCode: y.string().required(),
            addressState: y.string().required(),
            addressStreetLine1: y.string().required(),
            urla2020StreetAddress: y.string().required(),
            country: y.string().optional(),
          });
        }
        return y.object({
          addressCity: y.string().optional(),
          addressPostalCode: y.string().optional(),
          addressState: y.string().optional(),
          addressStreetLine1: y.string().optional(),
          urla2020StreetAddress: y.string().optional(),
          country: y.string().optional(),
        });
      },
    ),
  // declarations
  // questions-1
  partyToLawsuitIndicatorUrla: y.boolean().optional(),
  sectionIExplanation: y.string().optional(),
  outstandingJudgementsIndicator: y.boolean().optional(),
  sectionGExplanation: y.string().optional(),
  borrowedDownPaymentIndicator: y.boolean().optional(),
  sectionCExplanation: y.string().optional(),
  undisclosedBorrowedFundsIndicator: y.boolean().optional(),
  undisclosedBorrowedFundsAmount: y.number().optional(),
  coMakerEndorserOfNoteIndicator: y.boolean().optional(),
  sectionFExplanation: y.string().optional(),
  undisclosedComakerOfNoteIndicator: y.boolean().optional(),
  specialBorrowerSellerRelationshipIndicator: y.boolean().optional(),
  undisclosedMortgageApplicationIndicator: y.boolean().optional(),
  undisclosedCreditApplicationIndicator: y.boolean().optional(),
  // questions-2
  alimonyChildSupportObligationIndicator: y.boolean().optional(),
  bankruptcyIndicator: y.boolean().optional(),
  bankruptcyIndicatorChapterSeven: y.boolean().optional(),
  bankruptcyIndicatorChapterEleven: y.boolean().optional(),
  bankruptcyIndicatorChapterTwelve: y.boolean().optional(),
  bankruptcyIndicatorChapterThirteen: y.boolean().optional(),
  priorPropertyShortSaleCompletedIndicator: y.boolean().optional(),
  sectionKExplanation: y.string().optional(),
  priorPropertyForeclosureCompletedIndicator: y.boolean().optional(),
  sectionLExplanation: y.string().optional(),
  priorPropertyDeedInLieuConveyedIndicator: y.boolean().optional(),
  sectionJExplanation: y.string().optional(),
  loanForeclosureOrJudgementIndicator: y.boolean().optional(),
  presentlyDelinquentIndicatorUrla: y.boolean().optional(),
  sectionHExplanation: y.string().optional(),
  propertyProposedCleanEnergyLienIndicator: y.boolean().optional(),
  selfDeclaredMilitaryServiceIndicator: y.boolean().optional(),
  militaryServiceExpectedCompletionDate: y.string().optional(),
  veteran: y.boolean().optional(),
  reserveNationalGuardReserveActivated: y.boolean().optional(),
  spousalVaBenefitsEligibilityIndicator: y.boolean().optional(),
  // ethnicity
  hmdaEthnicityHispanicLatinoIndicator: y.boolean().optional(),
  hmdaMexicanIndicator: y.boolean().optional(),
  hmdaPuertoRicanIndicator: y.boolean().optional(),
  hmdaCubanIndicator: y.boolean().optional(),
  hmdaHispanicLatinoOtherOriginIndicator: y.boolean().optional(),
  hmdaOtherHispanicLatinoOrigin: y
    .string()
    .optional()
    .when(
      'hmdaHispanicLatinoOtherOriginIndicator',
      ([hmdaHispanicLatinoOtherOriginIndicator]) => {
        if (hmdaHispanicLatinoOtherOriginIndicator === true) {
          return y.string().required();
        }
        return y.string().optional();
      },
    ),
  hmdaEthnicityNotHispanicLatinoIndicator: y.boolean().optional(),
  hmdaEthnicityDoNotWishIndicator: y.boolean().optional(),
  // race
  hmdaAmericanIndianIndicator: y.boolean().optional(),
  hmdaAmericanIndianTribe: y
    .string()
    .optional()
    .when('hmdaAmericanIndianIndicator', ([hmdaAmericanIndianIndicator]) => {
      if (hmdaAmericanIndianIndicator === true) {
        return y.string().required();
      }
      return y.string().optional();
    }),
  hmdaAsianIndicator: y.boolean().optional(),
  hmdaAsianIndianIndicator: y.boolean().optional(),
  hmdaChineseIndicator: y.boolean().optional(),
  hmdaFilipinoIndicator: y.boolean().optional(),
  hmdaJapaneseIndicator: y.boolean().optional(),
  hmdaKoreanIndicator: y.boolean().optional(),
  hmdaVietnameseIndicator: y.boolean().optional(),
  hmdaAsianOtherRaceIndicator: y.boolean().optional(),
  hmdaOtherAsianRace: y
    .string()
    .optional()
    .when('hmdaAsianOtherRaceIndicator', ([hmdaAsianOtherRaceIndicator]) => {
      if (hmdaAsianOtherRaceIndicator === true) {
        return y.string().required();
      }
      return y.string().optional();
    }),
  hmdaAfricanAmericanIndicator: y.boolean().optional(),
  hmdaPacificIslanderIndicator: y.boolean().optional(),
  hmdaNativeHawaiianIndicator: y.boolean().optional(),
  hmdaGuamanianOrChamorroIndicator: y.boolean().optional(),
  hmdaSamoanIndicator: y.boolean().optional(),
  hmdaPacificIslanderOtherIndicator: y.boolean().optional(),
  hmdaOtherPacificIslanderRace: y
    .string()
    .optional()
    .when(
      ['hmdaPacificIslanderOtherIndicator'],
      ([hmdaPacificIslanderOtherIndicator]) => {
        if (hmdaPacificIslanderOtherIndicator === true) {
          return y.string().required();
        }
        return y.string().optional();
      },
    ),
  hmdaWhiteIndicator: y.boolean().optional(),
  hmdaRaceDoNotWishProvideIndicator: y.boolean().optional(),
  // other
  languagePreference: y.string().optional(),
  languageCodeOtherDescription: y
    .string()
    .optional()
    .when(['languagePreference'], ([languagePreference]) => {
      if (languagePreference === 'OtherIndicator') {
        return y.string().required();
      }
      return y.string().optional();
    }),
  hmdaGendertypeMaleIndicator: y.boolean().optional(),
  hmdaGendertypeFemaleIndicator: y.boolean().optional(),
  hmdaGendertypeDoNotWishIndicator: y.boolean().optional(),
  taxIdentificationIdentifier: y
    .string()
    .matches(/^\d{3}-\d{2}-\d{4}$/, {
      excludeEmptyString: true,
      message: 'Please enter a valid SSN',
    })
    .required(),
  twoYearsMinimum: y.string().optional(),
});

export const StoreBorrowerContract = BorrowerContract.omit([
  'urlaAlternateNames',
  'residences',
  'employment',
]);

// borrower contract for the co-borrower applications
export const MinimalBorrowerContract = BorrowerContract.pick([
  'id',
  'firstName',
  'lastName',
  'emailAddressText',
]).shape({
  emailAddressText: y.string().optional(),
});

// coborrower (spouse) contract for the co-borrower applications
export const MinimalCoborrowerContract = MinimalBorrowerContract.shape({
  firstName: y.string().optional(),
  lastName: y.string().optional(),
});

export interface Borrower extends y.Asserts<typeof BorrowerContract> {}
export interface StoreBorrower
  extends y.Asserts<typeof StoreBorrowerContract> {}

export const borrowerNewKioskValidationErrorMap = {
  _entityName: 'Borrower',
  borrower: {
    contact: {
      currentApplication: {
        borrower: {
          firstName: 'Please enter your first name',
          lastName: 'Please enter your last name',
          emailAddressText: 'Please enter your email address',
          homePhoneNumber: 'Please enter your phone number',
          birthDate: 'Please select a valid birth date',
          maritalStatusType: 'Please select your marital status',
          taxIdentificationIdentifier:
            'Please enter your Social Security Number',
        },
      },
    },
    'home-address': {
      currentApplication: {
        borrower: {
          twoYearsMinimum: 'types.validation.address.twoYearsMinimum',
        },
      },
    },
    'mailing-address': {
      currentApplication: {
        borrower: {
          mailingAddress: {
            missing: 'Please enter your mailing address',
            addressStreetLine1: 'Please enter your mailing street address',
            addressCity: 'Please enter your mailing address city',
            addressState: 'Please select your mailing address state',
            addressPostalCode: 'Please enter your mailing address postal code',
          },
        },
      },
    },
  },
  declarations: {
    ethnicity: {
      currentApplication: {
        borrower: {
          hmdaOtherHispanicLatinoOrigin: 'Please provide your origin',
        },
      },
    },
    identity: {
      currentApplication: {
        borrower: {
          hmdaAmericanIndianTribe:
            'Please provide name of enrolled or principal tribe',
          hmdaOtherAsianRace: 'Please provide other asian race',
          hmdaOtherPacificIslanderRace:
            'Please provide other pacific islander race',
        },
      },
    },
    'language-preference': {
      currentApplication: {
        borrower: {
          languageCodeOtherDescription: 'Please enter your language preference',
        },
      },
    },
  },
};

export const borrowerNewValidationErrorMap = {
  _entityName: 'Borrower',
  borrower: {
    contact: {
      currentApplication: {
        borrower: {
          firstName: 'Please enter your first name',
          lastName: 'Please enter your last name',
          emailAddressText: 'Please enter your email address',
          homePhoneNumber: 'Please enter your phone number',
          birthDate: 'Please select a valid birth date',
          maritalStatusType: 'Please select your marital status',
          taxIdentificationIdentifier:
            'Please enter your Social Security Number',
        },
      },
    },
    'home-address': {
      currentApplication: {
        borrower: {
          twoYearsMinimum: 'types.validation.address.twoYearsMinimum',
        },
      },
    },
    'mailing-address': {
      currentApplication: {
        borrower: {
          mailingAddress: {
            missing: 'Please enter your mailing address',
            addressStreetLine1: 'Please enter your mailing street address',
            addressCity: 'Please enter your mailing address city',
            addressState: 'Please select your mailing address state',
            addressPostalCode: 'Please enter your mailing address postal code',
          },
        },
      },
    },
  },
  declarations: {
    ethnicity: {
      currentApplication: {
        borrower: {
          hmdaOtherHispanicLatinoOrigin: 'Please provide your origin',
        },
      },
    },
    identity: {
      currentApplication: {
        borrower: {
          hmdaAmericanIndianTribe:
            'Please provide name of enrolled or principal tribe',
          hmdaOtherAsianRace: 'Please provide other asian race',
          hmdaOtherPacificIslanderRace:
            'Please provide other pacific islander race',
        },
      },
    },
    'language-preference': {
      currentApplication: {
        borrower: {
          languageCodeOtherDescription: 'Please enter your language preference',
        },
      },
    },
  },
};

export const coborrowerNewKioskValidationErrorMap = {
  _entityName: 'CoBorrower',
  coborrower: {
    contact: {
      currentApplication: {
        coBorrower: {
          firstName: "Please enter your co-borrower's first name",
          lastName: "Please enter your co-borrower's last name",
          emailAddressText: "Please enter your co-borrower's email address",
          homePhoneNumber: "Please enter your co-borrower's phone number",
          birthDate: 'Please select a valid birth date for your co-borrower',
          maritalStatusType: "Please select your co-borrower's marital status",
          taxIdentificationIdentifier:
            "Please enter your co-borrower's Social Security Number",
        },
      },
    },
    'home-address': {
      currentApplication: {
        coBorrower: {
          twoYearsMinimum: 'types.validation.address.twoYearsMinimum',
        },
      },
    },
    'mailing-address': {
      currentApplication: {
        coBorrower: {
          mailingAddress: {
            missing: "Please enter your co-borrower's mailing address",
            addressStreetLine1:
              "Please enter your co-borrower's mailing street address",
            addressCity: "Please enter your co-borrower's mailing address city",
            addressState:
              "Please select your co-borrower's mailing address state",
            addressPostalCode:
              "Please enter your co-borrower's mailing address postal code",
          },
        },
      },
    },
  },
  declarations: {
    ethnicity: {
      currentApplication: {
        coBorrower: {
          hmdaOtherHispanicLatinoOrigin:
            "Please provide your co-borrower's origin",
        },
      },
    },
    identity: {
      currentApplication: {
        coBorrower: {
          hmdaAmericanIndianTribe:
            "Please provide name of your co-borrower's enrolled or principal tribe",
          hmdaOtherAsianRace:
            "Please provide your co-borrower's other asian race",
          hmdaOtherPacificIslanderRace:
            "Please provide your co-borrower's other pacific islander race",
        },
      },
    },
    'language-preference': {
      currentApplication: {
        coBorrower: {
          languageCodeOtherDescription:
            "Please enter your co-borrower's language preference",
        },
      },
    },
  },
};

export const coborrowerNewValidationErrorMap = {
  _entityName: 'CoBorrower',
  coborrower: {
    contact: {
      currentApplication: {
        coBorrower: {
          firstName: "Please enter your co-borrower's first name",
          lastName: "Please enter your co-borrower's last name",
          emailAddressText: "Please enter your co-borrower's email address",
          homePhoneNumber: "Please enter your co-borrower's phone number",
          birthDate: 'Please select a valid birth date for your co-borrower',
          maritalStatusType: "Please select your co-borrower's marital status",
          taxIdentificationIdentifier:
            "Please enter your co-borrower's Social Security Number",
        },
      },
    },
    'home-address': {
      currentApplication: {
        coBorrower: {
          twoYearsMinimum: 'types.validation.address.twoYearsMinimum',
        },
      },
    },
    'mailing-address': {
      currentApplication: {
        coBorrower: {
          mailingAddress: {
            missing: "Please enter your co-borrower's mailing address",
            addressStreetLine1:
              "Please enter your co-borrower's mailing street address",
            addressCity: "Please enter your co-borrower's mailing address city",
            addressState:
              "Please select your co-borrower's mailing address state",
            addressPostalCode:
              "Please enter your co-borrower's mailing address postal code",
          },
        },
      },
    },
  },
  declarations: {
    ethnicity: {
      currentApplication: {
        coBorrower: {
          hmdaOtherHispanicLatinoOrigin:
            "Please provide your co-borrower's origin",
        },
      },
    },
    identity: {
      currentApplication: {
        coBorrower: {
          hmdaAmericanIndianTribe:
            "Please provide name of your co-borrower's enrolled or principal tribe",
          hmdaOtherAsianRace:
            "Please provide your co-borrower's other asian race",
          hmdaOtherPacificIslanderRace:
            "Please provide your co-borrower's other pacific islander race",
        },
      },
    },
    'language-preference': {
      currentApplication: {
        coBorrower: {
          languageCodeOtherDescription:
            "Please enter your co-borrower's language preference",
        },
      },
    },
  },
};
